import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { fmtEur, fmtDate, monthLabel } from "@/lib/format";
import { Wallet, CheckCircle2, MailCheck, CalendarClock, Search, X } from "lucide-react";
import ProcessPaymentWizard from "@/components/wizards/ProcessPaymentWizard";

export default function Outstanding() {
  const [rows, setRows] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [wiz, setWiz] = useState(null); // {tenantId, payments, period, paymentId}
  const [q, setQ] = useState("");
  const [onlyLate, setOnlyLate] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`/payments/outstanding?scope=overdue`), api.get(`/payments/outstanding?scope=upcoming`)]);
    setRows(a.data); setUpcoming(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openWizard = async (p) => {
    const res = await api.get(`/tenants/${p.tenant_id}/payments`);
    setWiz({ tenantId: p.tenant_id, payments: res.data, period: p.period, paymentId: p.id });
  };

  const match = (p) => {
    const s = q.trim().toLowerCase();
    if (clientFilter && (p.client_name || "") !== clientFilter) return false;
    if (!s) return true;
    return [p.tenant_name, p.client_name, monthLabel(p.period), p.contract_ovk].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  };
  const fRows = useMemo(() => rows.filter(match), [rows, q, clientFilter]);
  const fUpcoming = useMemo(() => (onlyLate ? [] : upcoming.filter(match)), [upcoming, q, clientFilter, onlyLate]);
  const suggestions = useMemo(() => Array.from(new Set([...rows, ...upcoming].map((p) => p.tenant_name).filter(Boolean))).sort(), [rows, upcoming]);
  const clients = useMemo(() => Array.from(new Set([...rows, ...upcoming].map((p) => p.client_name).filter(Boolean))).sort(), [rows, upcoming]);

  const total = fRows.reduce((s, p) => s + (p.total_due || 0), 0);

  const Row = ({ p, upcomingRow }) => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border hover:bg-slate-50" data-testid={`outstanding-row-${p.id}`}>
      <div className="flex items-center gap-3">
        <span className={`h-2.5 w-2.5 rounded-full ${upcomingRow ? "bg-slate-300" : p.status === "late" ? "bg-red-500" : "bg-amber-500"}`} />
        <div>
          <button className="font-medium text-slate-800 hover:underline text-left" onClick={() => navigate(`/app/huurders/${p.tenant_id}`)}>{p.tenant_name}</button>
          <div className="text-xs text-slate-500">{p.client_name} • {monthLabel(p.period)} • vervalt {fmtDate(p.due_date)}{p.status === "partial" && " • deels betaald"}</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {p.penalty > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">boete {fmtEur(p.penalty)}</Badge>}
        <div className="text-right"><div className="text-xs text-slate-400">Open</div><div className="font-bold">{fmtEur(p.total_due)}</div></div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openWizard(p)} data-testid={`process-payment-${p.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Verwerk</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Openstaand</h1>
        <p className="text-slate-500">Alle openstaande termijnen van álle debiteuren. Verwerk (deel)betalingen handmatig.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input list="outstanding-debtors" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op debiteur, opdrachtgever, maand of OVK…" className="pl-9 pr-9" data-testid="outstanding-search-input" />
        <datalist id="outstanding-debtors">
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="outstanding-search-clear"><X className="h-4 w-4" /></button>}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="outstanding-quick-filters">
        <button onClick={() => setOnlyLate((v) => !v)} data-testid="filter-only-late"
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${onlyLate ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200 hover:border-red-300"}`}>
          Alleen te laat
        </button>
        <span className="text-slate-300">|</span>
        <button onClick={() => setClientFilter("")} data-testid="filter-client-all"
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${!clientFilter ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          Alle opdrachtgevers
        </button>
        {clients.map((cl) => (
          <button key={cl} onClick={() => setClientFilter(cl === clientFilter ? "" : cl)} data-testid={`filter-client-${cl}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${clientFilter === cl ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>
            {cl}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800">
        <MailCheck className="h-4 w-4" /> Bij het verwerken van een betaling wordt automatisch een bevestiging per e-mail verstuurd.
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-red-700">Te laat ({fRows.length})</h3>
          <div className="text-right"><div className="text-xs text-slate-400">Totaal te laat</div><div className="text-lg font-bold text-red-600">{fmtEur(total)}</div></div>
        </div>
        {fRows.length === 0 ? <div className="text-center py-8 text-slate-400"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-40" /> {q ? "Geen resultaten voor deze zoekopdracht." : "Niets te laat. 🎉"}</div> :
          <div className="space-y-2">{fRows.map((p) => <Row key={p.id} p={p} />)}</div>}
      </Card>

      {!onlyLate && (
        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Aankomende betalingen ({fUpcoming.length})</h3>
          {fUpcoming.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">{q || clientFilter ? "Geen resultaten voor dit filter." : "Geen aankomende termijnen."}</p> :
            <div className="space-y-2">{fUpcoming.map((p) => <Row key={p.id} p={p} upcomingRow />)}</div>}
        </Card>
      )}

      {wiz && <ProcessPaymentWizard open={!!wiz} onOpenChange={(o) => !o && setWiz(null)} tenantId={wiz.tenantId} payments={wiz.payments} presetPeriod={wiz.period} presetPaymentId={wiz.paymentId} onSaved={load} />}
    </div>
  );
}
